<template>
  <card data-cy="warranty-program-card" headless>
    <template #body>
      <div class="divide-y *:pt-4 md:flex-row md:divide-y-0 *:md:pt-0">
        <div
          class="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 md:border-b md:mb-4"
        >
          <h4 class="mb-0">
            <div class="d-flex align-items-center">
              <div class="warrantyProgramCardTitle break-long-word">
                <ArchivedLabel :disabled="warrantyProgram.is_active">
                  {{ warrantyProgram.name }}
                </ArchivedLabel>
              </div>
              <a :href="warrantyProgramShowPath(warrantyProgram.id)"
                ><i class="fas fa-edit link edit-icon ml-1"></i
              ></a>
            </div>
          </h4>
          <div class="flex items-center">
            <QuickBooksMapping
              v-if="warrantyProgram.qbo_mapping"
              class="mapping mr-4"
              :item="warrantyProgram"
              @unlink-success="handleUnlinkSuccess"
            />
            <toggle-switch v-model="active" label-on="Active" />
            <i
              :class="[
                'fa-solid text-primary ml-4 cursor-pointer',
                { 'fa-chevron-down': !detailsOpen },
                { 'fa-chevron-up': detailsOpen }
              ]"
              @click="toggleDetails"
            ></i>
          </div>
        </div>
        <Transition name="slide-down-fade">
          <div
            v-if="detailsOpen"
            class="flex flex-col gap-4 divide-y *:pt-4 md:flex-row md:divide-x md:divide-y-0 *:md:pl-4 *:md:pt-0"
          >
            <div class="item-section">
              <div class="mb-2">
                <div><b>Provider: </b>{{ warrantyProgram.provider_name }}</div>
              </div>
              <div class="mb-2">
                <div><b>SKU Number: </b>{{ warrantyProgram.sku_number }}</div>
              </div>
              <div class="mb-2">
                <div>
                  <b>Labor Rate: </b>
                  {{ dollars(warrantyProgram.labor_rate_cents) }}
                </div>
              </div>
              <div class="mb-2">
                <div>
                  <b>Cost</b>
                  <Tooltip placement="top">
                    <template #trigger>
                      <i class="fas fa-info-circle" />
                    </template>
                    <template #tooltip>
                      The price to purchase the warranty program from the
                      warranty provider
                    </template>
                  </Tooltip>
                  <span>: {{ dollars(warrantyProgram.cost_cents) }}</span>
                </div>
              </div>
              <div class="mb-2">
                <div>
                  <b>Taxable</b>
                  <span>: {{ warrantyProgram.taxable ? "Yes" : "No" }}</span>
                </div>
              </div>
              <div class="mb-2">
                <div>
                  <b>Purchase Price</b>
                  <Tooltip placement="top">
                    <template #trigger>
                      <i class="fas fa-info-circle" />
                    </template>
                    <template #tooltip>
                      The price the customer pays to purchase the warranty
                    </template>
                  </Tooltip>
                  <span>: {{ dollars(warrantyProgram.price_cents) }}</span>
                </div>
              </div>
              <div class="mb-2">
                <div><b>Term (Years): </b>{{ warrantyProgram.term_years }}</div>
              </div>
              <div class="mb-2">
                <div>
                  <b>Effective After (Days)</b>
                  <Tooltip placement="top">
                    <template #trigger>
                      <i class="fas fa-info-circle" />
                    </template>
                    <template #tooltip>
                      How many days until the warranty program is effective
                      after the purchase date
                    </template>
                  </Tooltip>
                  <span
                    >: {{ warrantyProgram.effective_after_days || "--" }}</span
                  >
                </div>
              </div>
              <div v-if="showAccountingCategories" class="mb-2">
                <div>
                  <b>Accounting Category: </b>
                  {{ warrantyProgram.accounting_category_name }}
                </div>
              </div>
              <div v-if="showAccountingClasses" class="mb-2">
                <div>
                  <b>Default Accounting Class: </b>
                  {{ warrantyProgram.accounting_class_name || "--" }}
                </div>
              </div>
            </div>
            <div class="item-section flex-grow-2">
              <div class="mb-2"><b>Coverage Description</b></div>
              <div class="pre-line">{{ warrantyProgram.description }}</div>
            </div>
            <div class="item-section flex-grow-2">
              <div class="mb-2"><b>Included Equipment Type(s)</b></div>
              <div class="mb-2">{{ equipmentTypesLabel }}</div>
              <div class="mb-2">
                <EntityIncludedIn :included-in="warrantyProgram.included_in" />
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </template>
  </card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import Card from "@/components/Card.vue";
import EntityIncludedIn from "@/components/EntityIncludedIn.vue";
import FeatureFlag from "@/components/FeatureFlag.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import Tooltip from "@/components/Tooltip.vue";
import Transition from "@/components/Transition.vue";
import QuickBooksMapping from "@/Quickbooks/QuickBooksMapping.vue";

export default {
  components: {
    QuickBooksMapping,
    FeatureFlag,
    Card,
    ToggleSwitch,
    Transition,
    Tooltip,
    EntityIncludedIn
  },
  props: {
    warrantyProgram: {
      type: Object,
      default: () => ({})
    },
    defaultOpen: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      active: this.warrantyProgram.is_active,
      rejectedUpdateActive: false,
      detailsOpen: this.defaultOpen
    };
  },
  computed: {
    ...mapGetters(["showAccountingCategories", "showAccountingClasses"]),
    equipmentTypesLabel() {
      if (!this.warrantyProgram.equipment_types.length) return "--";
      return this.warrantyProgram.equipment_types
        .map((et) => et.name)
        .join(", ");
    }
  },
  watch: {
    active(newValue, oldValue) {
      if (this.rejectedUpdateActive) {
        this.rejectedUpdateActive = false;
      } else if (newValue !== oldValue) {
        this.handleActiveChange();
      }
    }
  },
  methods: {
    ...mapActions([
      "fetchWarrantyPrograms",
      "updateWarrantyProgram",
      "confirmChanges"
    ]),
    async handleActiveChange() {
      if (
        this.warrantyProgram.included_in.length > 0 &&
        this.warrantyProgram.is_active &&
        !this.active
      ) {
        try {
          await this.confirmChanges({
            show: true,
            title: "Inactivate Warranty Program",
            message: `Inactivating this program will unlink it from ${this.includedTasksCount()} task(s). Are you sure?`
          });

          this.updateWarrantyActive();
        } catch (e) {
          this.rejectedUpdateActive = true;
          this.active = this.warrantyProgram.is_active;
        }
      } else {
        this.updateWarrantyActive();
      }
    },
    toggleDetails() {
      this.detailsOpen = !this.detailsOpen;
    },
    updateWarrantyActive() {
      this.updateWarrantyProgram({
        id: this.warrantyProgram.id,
        is_active: this.active
      });
    },
    handleUnlinkSuccess() {
      this.fetchWarrantyPrograms();
    },
    includedTasksCount() {
      return this.warrantyProgram.included_in.reduce(
        (accSum, { count }) => accSum + count,
        0
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.warrantyProgramCardTitle {
  max-width: 600px;
}
.flex-grow-2 {
  flex-grow: 2;
}

.italic-text {
  font-style: italic;
}

.edit-icon {
  font-size: 0.9rem;
  position: relative;
  top: -3px;
}

.program-type-text {
  color: var(--gray);
  font-style: italic;
}

.center-text-section {
  text-align: center;
}

.mapping {
  position: relative;
  top: 2px;
}
</style>
